(() => {
  let dispose = () => {};

  function initialize() {
    dispose();
    const header = document.querySelector('[data-mr-header]');
    if (!header) return;
    const root = document.documentElement;
    const search = header.querySelector('[data-mr-search]');
    const input = search?.querySelector('input[type="search"]');
    const suggestions = search?.querySelector('[data-mr-search-suggestions]');
    const content = search?.querySelector('[data-mr-search-suggestion-content]');
    const predictive = search?.querySelector('predictive-search');
    const menus = Array.from(header.querySelectorAll('.header__primary-nav details'));
    const panels = Array.from(header.querySelectorAll('.mega-menu, .header__dropdown-menu'));
    const sidebar = header.querySelector('header-sidebar');
    const cart = document.querySelector('cart-drawer');
    const dialogs = [search, sidebar, cart].filter(Boolean);
    const hover = window.matchMedia('(min-width: 1000px) and (hover: hover) and (pointer: fine)');
    const controller = new AbortController();
    const signal = controller.signal;
    // Keep native horizontal trackpad/touch movement; a mouse wheel can also browse capsules.
    header.querySelectorAll('.mr-capsule-menu__carousel').forEach((carousel) => {
      carousel.addEventListener('wheel', (event) => {
        if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
        const max = carousel.scrollWidth - carousel.clientWidth;
        if (max <= 1) return;
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? carousel.clientWidth : 1;
        const next = Math.max(0, Math.min(max, carousel.scrollLeft + event.deltaY * unit));
        if (Math.abs(next - carousel.scrollLeft) < 1) return;
        event.preventDefault();
        carousel.scrollLeft = next;
      }, { passive: false, signal });
    });
    const backdrop = document.createElement('div');
    backdrop.className = 'mr-page-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.append(backdrop);
    let frame = 0;
    let keyboard = false;
    let lastState = '';
    let lastLoaded = 0;
    let pending = null;

    function sync() {
      frame = 0;
      const openMenus = menus.filter((menu) => menu.getAttribute('aria-expanded') === 'true' && menu.getBoundingClientRect().width > 0);
      if (openMenus.length && search?.hasAttribute('open')) search.hide();
      // Keep the surface in place until the native closing animation completes.
      const searchVisible = !!search && search.offsetHeight > 0;
      const mobilePanels = [sidebar, cart].filter((panel) => panel?.hasAttribute('data-mr-header-panel'));
      const mobileVisible = mobilePanels.some((panel) => panel.offsetHeight > 0);
      const bottom = header.getBoundingClientRect().bottom;
      // Native desktop drawers have zero-height hosts; their fixed shadow
      // surface remains visible through the closing animation.
      const backgroundBlurred = openMenus.length > 0 || dialogs.some((dialog) =>
        dialog.hasAttribute('open') || dialog.offsetHeight > 0 || dialog.getShadowPartByName?.('content')?.offsetHeight > 0
      );
      backdrop.style.top = `${Math.max(0, bottom)}px`;
      root.classList.toggle('mr-page-panel-open', backgroundBlurred);
      header.style.setProperty('--mr-panel-max-height', `${Math.max(120, (window.visualViewport?.height || window.innerHeight) - bottom)}px`);
      const engaged = (hover.matches && header.matches(':hover')) ||
        (keyboard && header.contains(document.activeElement)) || openMenus.length > 0 ||
        searchVisible || mobileVisible || !!sidebar?.hasAttribute('open');
      const visiblePanels = openMenus.map((menu) => menu.querySelector('.mega-menu, .header__dropdown-menu')).filter(Boolean);
      if (searchVisible) visiblePanels.push(search);
      visiblePanels.push(...mobilePanels.filter((panel) => panel.hasAttribute('open')));
      const height = Math.ceil(Math.max(0, ...visiblePanels.map((panel) => panel.offsetHeight)));
      header.style.setProperty('--mr-panel-height', `${height}px`);
      header.classList.toggle('is-engaged', engaged);
      root.classList.toggle('mr-header-engaged', engaged);
      const state = `${engaged}:${height}:${searchVisible}:${backgroundBlurred}`;
      if (state !== lastState) {
        lastState = state;
        header.dispatchEvent(new CustomEvent('mr:header-state'));
      }
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(sync);
    }

    async function loadSuggestions() {
      if (!search || !content || !suggestions || signal.aborted) return;
      if (pending || (lastLoaded && Date.now() - lastLoaded < 60000)) return pending;
      content.setAttribute('aria-busy', 'true');
      pending = (async () => {
        try {
          const response = await fetch(search.dataset.suggestionsUrl, { credentials: 'same-origin', signal });
          if (!response.ok) throw new Error('Could not load search suggestions');
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const products = page.querySelector('[data-mr-search-products]');
          if (!products) throw new Error('Missing search suggestions');
          content.replaceChildren(document.importNode(products, true));
          lastLoaded = Date.now();
        } catch (error) {
          if (error.name !== 'AbortError') suggestions.hidden = true;
        } finally {
          pending = null;
          content.removeAttribute('aria-busy');
          schedule();
        }
      })();
      return pending;
    }

    function syncQuery() {
      if (!input) return;
      const empty = input.value.trim() === '';
      suggestions.hidden = !empty;
      predictive.hidden = empty;
      if (empty && search.hasAttribute('open')) loadSuggestions();
      schedule();
    }

    header.addEventListener('pointerenter', schedule, { signal });
    header.addEventListener('pointerleave', schedule, { signal });
    header.addEventListener('focusin', schedule, { signal });
    header.addEventListener('focusout', schedule, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') { keyboard = true; schedule(); }
    }, { signal });
    document.addEventListener('pointerdown', () => { keyboard = false; schedule(); }, { passive: true, signal });
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true, signal });
    hover.addEventListener('change', schedule, { signal });

    function closeOtherPanels(opening) {
      dialogs.filter((dialog) => dialog !== opening && dialog.hasAttribute('open')).forEach((dialog) => dialog.hide());
      menus.filter((menu) => menu.open).forEach((menu) => menu.closest('mega-menu-disclosure')?.close());
      schedule();
    }
    dialogs.forEach((dialog) => {
      dialog.addEventListener('dialog:before-show', () => closeOtherPanels(dialog), { signal });
      ['dialog:after-show', 'dialog:before-hide', 'dialog:after-hide'].forEach((name) => dialog.addEventListener(name, schedule, { signal }));
    });

    const resize = new ResizeObserver(schedule);
    resize.observe(header);
    panels.forEach((panel) => resize.observe(panel));
    if (search) {
      resize.observe(search);
      const state = new MutationObserver(schedule);
      state.observe(search, { attributes: true, attributeFilter: ['open', 'style'] });
      search.addEventListener('dialog:before-show', () => {
        menus.filter((menu) => menu.open).forEach((menu) => menu.closest('mega-menu-disclosure')?.close());
        loadSuggestions();
        syncQuery();
        schedule();
      }, { signal });
      search.addEventListener('dialog:after-show', schedule, { signal });
      search.addEventListener('dialog:after-hide', schedule, { signal });
      input.addEventListener('input', syncQuery, { signal });
      input.form.addEventListener('reset', () => queueMicrotask(syncQuery), { signal });
      header.querySelectorAll(`[aria-controls="${search.id}"]`).forEach((link) => {
        link.addEventListener('pointerenter', loadSuggestions, { signal });
        link.addEventListener('focus', loadSuggestions, { signal });
      });
      signal.addEventListener('abort', () => state.disconnect(), { once: true });
      syncQuery();
    }
    const state = new MutationObserver(schedule);
    menus.forEach((menu) => state.observe(menu, { attributes: true, attributeFilter: ['open', 'aria-expanded'] }));
    [sidebar, cart].filter(Boolean).forEach((panel) => {
      resize.observe(panel);
      state.observe(panel, { attributes: true, attributeFilter: ['open', 'style', 'data-mr-header-panel'] });
    });

    dispose = () => {
      controller.abort();
      resize.disconnect();
      state.disconnect();
      cancelAnimationFrame(frame);
      root.classList.remove('mr-header-engaged', 'mr-page-panel-open');
      backdrop.remove();
      header.classList.remove('is-engaged');
      header.style.removeProperty('--mr-panel-height');
      header.style.removeProperty('--mr-panel-max-height');
    };
    sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-mr-header]')) dispose();
  });
})();
